import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable, NgZone, computed, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { Observable, Subject, filter, firstValueFrom, takeUntil } from 'rxjs';
import { isOfflineCapableNative } from '../commons/utils/platform';
import { NetworkStatusService } from '../services/network-status.service';
import { MasterCacheService } from './master-cache.service';
import { ListCacheService } from './list-cache.service';
import { BusinessPartnerService } from '../modules/business-partner/business-partner.service';
import { ProductService } from '../modules/product/services/product.service';
import { LoadingSiteService } from '../modules/loading-site/services/loading-site.service';
import { DashboardV6Service } from '../modules/dashboard-v6/services/dashboard-v6.service';
import { toLocalDateString } from '../commons/utils/date-utils';
import { TransportUnitService } from '../modules/new-tallysheet/components/transport-unit.service';
import { TallysheetListService } from '../modules/new-tallysheet/services/tallysheet-list/tallysheet-list.service';
import { InventoryService } from '../modules/inventory/services/inventory.service';
import { InventoryLotService } from '../modules/inventory/services/inventory-lot.service';
import { InventoryModuleService } from '../services/inventory-module/inventory-module.service';
import { PermissionService } from '../services/permission.service';
import { AppConstants } from '../commons/app-constants';
import { Page } from '../commons/models/page';
import { offlineDb, scopedKey, tidyOfflineFiles, wipeOfflineDataIfDifferentUser } from './offline-db';
import { SyncEngineService } from './sync-engine.service';
import {
  TU_WINDOW_DAYS, TU_WINDOW_MAX, getTuPins, getTuWindowMarks, saveTuWindow, saveTuWindowMarks, tuFingerprint,
} from './offline-window';
import { App } from '@capacitor/app';
import { runDeltaListSync } from './delta-merge.util';
import { PrefetchGateService } from './prefetch-gate.service';
import { onOfflineSyncCancel } from './offline-sync-control';
import { putPrefilled, setAnswerTracking, wasAnsweredSince } from './prefill-store';
import { DashboardEventSection, DashboardSocketService } from '../modules/dashboard-v6/services/dashboard-socket.service';
import { TallyUpdateService } from '../modules/new-tallysheet/tally-update.service';

// Same cap as BusinessPartnerService.cacheAttachmentBlobs / LoadingSiteService.cacheDocumentBlobs.
const MAX_CACHE_FILE_SIZE = 10 * 1024 * 1024;

export type SyncAreaStatus = 'pending' | 'running' | 'done' | 'error';
export interface SyncAreaState {
  labelKey: string;
  status: SyncAreaStatus;
  /** Live count for a long area (Stock Units: "45 / 180", 2026-09-29) — shown only while total > 0. */
  done?: number;
  total?: number;
}

// One row per user-visible area — NOT one row per internal sub-fetch (master dropdowns/
// dashboard stats complete silently in the background with no row of their own; TU's row
// covers list+detail+full tallysheet as a single unit). Each row is flipped independently by
// its own batch promise (see runArea()) — there is no shared "current step" pointer, which is
// what lets this read sensibly even though the underlying batch runs concurrently and areas
// can finish in any order.
const INITIAL_SYNC_AREAS: SyncAreaState[] = [
  { labelKey: 'common.syncAreaBusinessPartners', status: 'pending' },
  { labelKey: 'common.syncAreaProducts', status: 'pending' },
  { labelKey: 'common.syncAreaLoadingSites', status: 'pending' },
  { labelKey: 'common.syncAreaTransportUnits', status: 'pending' },
  { labelKey: 'common.syncAreaDashboard', status: 'pending' },
];

// Proactively warms the SAME offline caches that today only get populated by manually visiting
// every page (MasterCacheService/ListCacheService/DetailCacheService — see each list
// component's own onLazyLoad + prefetchDetailCache). Native mobile only (Android/iOS); bounded to
// master dropdowns + top-10 window of each list (kept in sync via true delta-sync, Increment 6 —
// see runDeltaListSync) + those rows' detail records + default dashboard stats. Runs on every
// online transition (see the constructor's effect() below), not on a time-based cooldown.
@Injectable({ providedIn: 'root' })
export class OfflinePrefetchService {
  private readonly http = inject(HttpClient);
  private readonly networkStatusService = inject(NetworkStatusService);
  private readonly masterCacheService = inject(MasterCacheService);
  private readonly listCacheService = inject(ListCacheService);
  private readonly businessPartnerService = inject(BusinessPartnerService);
  private readonly productService = inject(ProductService);
  private readonly loadingSiteService = inject(LoadingSiteService);
  private readonly dashboardV6Service = inject(DashboardV6Service);
  private readonly transportUnitService = inject(TransportUnitService);
  private readonly tallysheetListService = inject(TallysheetListService);
  private readonly inventoryService = inject(InventoryService);
  private readonly inventoryLotService = inject(InventoryLotService);
  private readonly inventoryModuleService = inject(InventoryModuleService);
  private readonly permissionService = inject(PermissionService);
  private readonly router = inject(Router);
  private readonly logger = inject(NGXLogger);
  private readonly prefetchGate = inject(PrefetchGateService);
  private readonly zone = inject(NgZone);
  private readonly syncEngine = inject(SyncEngineService);
  private readonly liveSocket = inject(DashboardSocketService);
  private readonly tallyUpdateService = inject(TallyUpdateService);
  private inFlight = false;

  // Run id (OFFLINE_SYNC_SPEED_PLAN_2026-10-03.md, step A). Bumped when the session ends or the identity is about to
  // be wiped (see offline-sync-control.ts). Every step of a run compares the id it started with against this one and
  // stops, writes nothing and touches no checklist row when they differ — so a logout or user/company switch ends the
  // run at once and a late answer from the old session can never land in the new one.
  private runEpoch = 0;

  // Fires when the session ends: requests of this sync that are already on the wire are cancelled (not only ignored), so no
  // answer of the old session is received or written after the logout / user switch. Used through once().
  private readonly cancelled$ = new Subject<void>();

  // Offline bundle (Revision 4 of OFFLINE_SYNC_SPEED_PLAN_2026-10-03.md): the answers of 5 units in ONE request, kept in the prefill
  // store for the normal per-unit code. Only a faster way to get the same answers: any problem (server flag off, older server, error,
  // a part that did not come, a path that does not match) simply means the normal calls are made.
  private static readonly BUNDLE_CHUNK = 5;
  private bundleUnavailable = false;
  private bundleFailures = 0;
  private bundleRequests = 0;

  // How long a unit's saved copy may take to show up after the server answered (the cache write is not awaited by the callers, and on
  // the phone's encrypted database it can be slow), and how long to look when there is no proof of an answer (an old copy handed back
  // by a fallback never turns fresh, so there is no point in waiting). static, not readonly: the tests shorten them.
  private static WRITE_WAIT_MS = 4_000;
  private static NO_ANSWER_WAIT_MS = 300;

  // One line per run for the owner / support (see logRunSummary): what was saved completely and why anything was not.
  private stats = { trigger: 'online', units: 0, complete: 0, incomplete: 0, gaveUp: 0, reasons: {} as Record<string, number> };
  private noteIncomplete(reason: string): void {
    this.stats.reasons[reason] = (this.stats.reasons[reason] ?? 0) + 1;
  }
  private noteUnit(saved: boolean): void {
    this.stats.units++;
    if (saved) this.stats.complete++;
    else this.stats.incomplete++;
  }
  private logRunSummary(startedAt: number): void {
    const s = this.stats;
    const line = `[OfflinePrefetchService] sync summary: trigger=${s.trigger} units=${s.units} complete=${s.complete} incomplete=${s.incomplete} acceptedAsIs=${s.gaveUp} ` +
      `bundle=${this.bundleUnavailable ? 'off' : 'on'} bundleRequests=${this.bundleRequests} seconds=${Math.round((Date.now() - startedAt) / 1000)} reasons=${JSON.stringify(s.reasons)}`;
    this.logger.info(line);
    console.info(line); // also visible in the phone's remote inspector whatever the log level
  }

  // Live-update trigger (S5, OFFLINE_SYNC_SPEED_PLAN_2026-10-03.md): when the server announces that consignments / units, tally
  // creation or deletion, or master data (business partners, products, loading sites) changed, the phone refreshes its offline copy
  // by itself. Tally ROW saves are deliberately not listened to (they come every few seconds while someone tallies): rows are
  // refreshed by the other triggers (back online, app back in front, "Sync now"), as before.
  // Loop protection, whatever the server does: changes in a short burst become ONE run (settle time), a run never starts sooner than
  // MIN_GAP after the previous one started, and at most MAX_RUNS live-triggered runs start in any WINDOW. (The sync only reads, and
  // the server does not announce the one write a read can cause - creating default tally settings - but the guard does not rely on it.)
  private static readonly LIVE_SECTIONS: DashboardEventSection[] = ['CONSIGNMENT', 'TALLY_STATUS', 'MASTER_DATA'];
  private static LIVE_SETTLE_MS = 3_000; // static, not readonly: the tests shorten these
  private static LIVE_MIN_GAP_MS = 60_000;
  private static LIVE_WINDOW_MS = 10 * 60_000;
  private static LIVE_MAX_RUNS = 6;
  // A live-triggered run waits while a tally row was saved on this phone in the last 30 s (someone is tallying): the sync adds main
  // thread work and network at the moment the keypad needs both. It starts as soon as the tallying pauses.
  private static LIVE_TALLY_QUIET_MS = 30_000;
  private lastTallySaveAt = 0;
  private liveSyncWanted = false;
  private liveTimer: ReturnType<typeof setTimeout> | null = null;
  private liveRunStarts: number[] = [];
  private releaseLiveInterest: (() => void) | null = null;
  private once<T>(source: Observable<T>): Promise<T> {
    return firstValueFrom(source.pipe(takeUntil(this.cancelled$)));
  }

  // One running total for the Stock Units row: units whose detail + tabs are being warmed in this run (the newest ten
  // plus the rest of the offline window), shown as "done / total" as soon as the total is known.
  private static readonly TU_AREA = 'common.syncAreaTransportUnits';
  private tuDone = 0;
  private tuTotal = 0;

  /** Drives the per-area checklist in app.component.html — replaces the old single boolean. */
  readonly syncAreas = signal<SyncAreaState[]>(INITIAL_SYNC_AREAS);
  /** True while any area is still running — derived, not independently set, so there's exactly one source of truth. */
  readonly isRunning = computed(() => this.syncAreas().some(a => a.status === 'running'));

  // Same "check the current route" style already used by TallysheetHttpInterceptorService's
  // isSessionExpiredDialogSuppressedRoute() — router.url, not any auth/company API call, so
  // this stays a cheap synchronous check on every online-transition tick.
  private isOnboardingInProgressRoute(): boolean {
    const url = this.router.url;
    return url.startsWith('/account-setup') || url.startsWith('/email-verification');
  }

  // runSync() runs entirely OUTSIDE Angular's zone (see the constructor) so the ~dozens of
  // await-resumptions in its prefetch tree don't each trigger an app-wide change-detection pass
  // over whatever heavy screen happens to be mounted — that CD storm, on top of the HTTP/parse/
  // IndexedDB work, is what still wedged the main thread on a slow device even after the leaf
  // calls were concurrency-limited. The only bit that must re-enter the zone is this checklist
  // signal write, so the "Preparing offline data…" strip actually repaints — there are only a
  // handful of these per run.
  private setAreaStatus(labelKey: string, status: SyncAreaStatus): void {
    this.zone.run(() => {
      this.syncAreas.update(areas => areas.map(a => a.labelKey === labelKey ? { ...a, status } : a));
    });
  }

  /** Live "done / total" on one area's row (2026-09-29) — at most ~5 repaints a second. */
  private lastCountAt = 0;
  private setAreaCount(labelKey: string, done: number, total: number, force = false): void {
    const now = performance.now();
    if (!force && now - this.lastCountAt < 200) return;
    this.lastCountAt = now;
    this.zone.run(() => {
      this.syncAreas.update(areas => areas.map(a => a.labelKey === labelKey ? { ...a, done, total } : a));
    });
  }

  // Resolves once the main thread has been idle (requestIdleCallback), or after IDLE_WAIT_CAP_MS,
  // whichever comes first. Used to hold the prefetch batch off the critical first-render path.
  private static readonly IDLE_WAIT_CAP_MS = 4000;
  private waitForIdle(): Promise<void> {
    return new Promise<void>((resolve) => {
      const ric = (globalThis as any).requestIdleCallback as
        undefined | ((cb: () => void, opts?: { timeout: number }) => void);
      if (typeof ric === 'function') {
        ric(() => resolve(), { timeout: OfflinePrefetchService.IDLE_WAIT_CAP_MS });
      } else {
        setTimeout(resolve, 3000);
      }
    });
  }

  // Wraps one area's prefetch work so its checklist row flips pending -> running -> done/error
  // independently of every other area — never rejects (matches the "swallow" convention used
  // everywhere else in this file), so one area's failure can't abort Promise.allSettled's
  // sibling entries or leave the batch stuck mid-flight.
  private async runArea(labelKey: string, work: () => Promise<unknown>): Promise<void> {
    const epoch = this.runEpoch;
    this.setAreaStatus(labelKey, 'running');
    try {
      await work();
      if (epoch === this.runEpoch) this.setAreaStatus(labelKey, 'done');
    } catch {
      if (epoch === this.runEpoch) this.setAreaStatus(labelKey, 'error');
    }
  }

  /** Asks the server for the answers of these units in one request and keeps them for the normal calls. Never throws. */
  private async loadBundle(rows: any[], epoch: number): Promise<number> {
    if (this.bundleUnavailable || epoch !== this.runEpoch) return 0;
    const ids: string[] = rows.map((r: any) => r?.encodedId).filter((id: any) => !!id);
    if (!ids.length) return 0;
    this.bundleRequests++;
    const url = `${AppConstants.API_URL}api/v1/transportunits/offline-bundle?encodedIds=${ids.map((id) => encodeURIComponent(id)).join(',')}`;
    try {
      const body: any = await this.prefetchGate.run(() => this.once(this.http.get<any>(url)));
      if (epoch !== this.runEpoch || body === undefined) return 0; // ended, or skipped by the paused / cancelled queue: normal calls
      if (body?.enabled === false) { this.bundleUnavailable = true; return 0; } // the server has the bundle switched off
      if (!Array.isArray(body?.units)) { this.bundleFailed(); return 0; }
      this.bundleFailures = 0;
      for (const unit of body.units) {
        for (const part of unit?.parts ?? []) {
          // Only a complete answer is kept; a part with an error status or without a body is fetched the normal way.
          if (part?.status === 200 && part.body !== undefined && part.url) putPrefilled(part.url, part.body);
        }
      }
      // How many of the asked units the server answered (it leaves units out when the rows get too many; they come in the next request).
      return body.units.length;
    } catch (err) {
      if (err instanceof HttpErrorResponse && (err.status === 404 || err.status === 405)) this.bundleUnavailable = true; // older server
      else this.bundleFailed();
      return 0;
    }
  }

  private bundleFailed(): void {
    if (++this.bundleFailures >= 2) this.bundleUnavailable = true; // two bad answers in a row: stop asking in this run
  }

  /** Warms units a few at a time: load a bundle for the next 5, warm those 5 (their calls are answered from the bundle), repeat.
   *  When the server offers no bundle the remaining units run all at once exactly as before. */
  private async warmInChunks(rows: any[], epoch: number, warmOne: (row: any) => Promise<void>): Promise<void> {
    const take = (from: number): any[] => rows.slice(from, from + OfflinePrefetchService.BUNDLE_CHUNK);
    let index = 0;
    let current = take(0);
    let served = current.length ? await this.loadBundle(current, epoch) : 0;
    while (current.length) {
      if (epoch !== this.runEpoch) return;
      if (this.bundleUnavailable) {
        // No bundles from this server: everything left runs at once, exactly as before.
        const rest = rows.slice(index);
        await Promise.allSettled(rest.map(warmOne));
        return;
      }
      // The units this answer covers (all of them, or fewer when the server cut it short; then the rest start the next request).
      // An answer for none (error) means those units are simply saved with the normal calls.
      const covered = served > 0 ? Math.min(served, current.length) : current.length;
      const mine = current.slice(0, covered);
      index += covered;
      // The next bundle is requested NOW, while this one's units are being saved from memory, so the waiting overlaps.
      const nextRows = take(index);
      const nextBundle = nextRows.length ? this.loadBundle(nextRows, epoch) : Promise.resolve(0);
      await Promise.allSettled(mine.map(warmOne));
      current = nextRows;
      served = await nextBundle; // an entry nobody asked for simply expires (30 s), nothing is cleared here
    }
  }

  /** Listens to the live connection (one shared connection, see DashboardSocketService). Never throws. */
  private startLiveTrigger(): void {
    try {
      // Tell the server which sections this phone wants (it sends each user only what their open screens asked for). The
      // declaration is repeated after every (re)connect: logout / reconnect clears it on the socket side.
      const declare = (): void => {
        this.releaseLiveInterest?.();
        this.releaseLiveInterest = this.liveSocket.declareInterest(OfflinePrefetchService.LIVE_SECTIONS);
      };
      declare();
      this.liveSocket.connected$.subscribe(() => declare());
      this.tallyUpdateService.saved$.subscribe(() => { this.lastTallySaveAt = Date.now(); });
      // 'RESYNC' = the socket caught up after a reconnect / a server restart: refresh too.
      this.liveSocket.changes$
        .pipe(filter((c) => c.sections.includes('RESYNC') || c.sections.some((s) => OfflinePrefetchService.LIVE_SECTIONS.includes(s))))
        .subscribe(() => this.onLiveChange());
    } catch (err) {
      this.logger.warn('[OfflinePrefetchService] live-update trigger not started', err);
    }
  }

  private onLiveChange(): void {
    if (!this.networkStatusService.isOnline) return;
    this.liveSyncWanted = true;
    this.scheduleLiveSync();
  }

  /** One pending start at most. Not while a run is going (its end schedules the follow-up). */
  private scheduleLiveSync(): void {
    if (!this.liveSyncWanted || this.liveTimer || this.inFlight) return;
    const sinceLast = Date.now() - this.lastSyncStartedAt;
    const wait = Math.max(OfflinePrefetchService.LIVE_SETTLE_MS, OfflinePrefetchService.LIVE_MIN_GAP_MS - sinceLast);
    this.liveTimer = setTimeout(() => { this.liveTimer = null; this.startLiveSync(); }, wait);
  }

  private startLiveSync(): void {
    if (!this.liveSyncWanted || !this.networkStatusService.isOnline) return; // a manual / online / resume run already did it, or we are offline (back online runs a sync anyway)
    const now = Date.now();
    const sinceTallySave = now - this.lastTallySaveAt;
    if (sinceTallySave < OfflinePrefetchService.LIVE_TALLY_QUIET_MS) {
      // Someone is tallying right now: wait for a pause (does not count towards the run limit).
      this.liveTimer = setTimeout(() => { this.liveTimer = null; this.startLiveSync(); }, OfflinePrefetchService.LIVE_TALLY_QUIET_MS - sinceTallySave + 500);
      return;
    }
    this.liveRunStarts = this.liveRunStarts.filter((t) => now - t < OfflinePrefetchService.LIVE_WINDOW_MS);
    if (this.liveRunStarts.length >= OfflinePrefetchService.LIVE_MAX_RUNS) {
      // Loop / flood protection: wait until the oldest live-triggered run leaves the window.
      this.logger.warn('[OfflinePrefetchService] live-update sync limit reached, waiting');
      const wait = OfflinePrefetchService.LIVE_WINDOW_MS - (now - this.liveRunStarts[0]) + 1000;
      this.liveTimer = setTimeout(() => { this.liveTimer = null; this.startLiveSync(); }, wait);
      return;
    }
    this.liveRunStarts.push(now);
    this.zone.runOutsideAngular(() => { void this.runSync(false, 'live'); });
  }

  /** The session ended / the identity is being wiped: stop this run at once, free the request queue, hide the strip. */
  private handleCancel(): void {
    this.runEpoch++;
    this.cancelled$.next();
    this.inFlight = false;
    this.lastSyncStartedAt = 0; // the next login may start its own run right away
    this.tuDone = 0;
    this.tuTotal = 0;
    this.liveSyncWanted = false;
    if (this.liveTimer) { clearTimeout(this.liveTimer); this.liveTimer = null; }
    setAnswerTracking(false);
    this.prefetchGate.cancelPending();
    this.zone.run(() => this.syncAreas.set(INITIAL_SYNC_AREAS.map(a => ({ ...a }))));
  }

  /** Adds units to the Stock Units total and shows it at once (not throttled: the first number must appear immediately). */
  private addTuTotal(count: number, epoch: number): void {
    if (epoch !== this.runEpoch || count <= 0) return;
    this.tuTotal += count;
    this.setAreaCount(OfflinePrefetchService.TU_AREA, this.tuDone, this.tuTotal, true);
  }

  private bumpTuDone(epoch: number): void {
    if (epoch !== this.runEpoch) return;
    this.tuDone++;
    this.setAreaCount(OfflinePrefetchService.TU_AREA, this.tuDone, this.tuTotal);
  }

  /** Shows the exact final number (the live updates are throttled to about 5 a second). */
  private flushTuCount(epoch: number): void {
    if (epoch !== this.runEpoch || this.tuTotal <= 0) return;
    this.setAreaCount(OfflinePrefetchService.TU_AREA, this.tuDone, this.tuTotal, true);
  }

  // Eagerly instantiated at app startup via the same constructor-injection trick as
  // SyncEngineService (see app.component.ts's own comment on injecting it) — this effect IS
  // the trigger now, there is no other entry point. Reacts to onlineTransitionTick() (an
  // already-debounced "we just settled into being online" signal), not the raw online()
  // level — a real reconnect should start a sync, but merely navigating between screens while
  // already online must not. This replaces the old 15-minute "don't sync too often" cooldown
  // entirely: there is no general time-based throttle left, only the single-flight guard below
  // and the debounce upstream in NetworkStatusService.
  constructor() {
    if (!isOfflineCapableNative()) return;
    // Logout / session end / user or company change: stop the run and hide the strip (offline-sync-control.ts).
    onOfflineSyncCancel(() => this.handleCancel());
    this.startLiveTrigger();
    effect(() => {
      this.networkStatusService.onlineTransitionTick(); // read to subscribe to changes
      // Whole batch runs outside Angular's zone — see setAreaStatus()'s comment.
      this.zone.runOutsideAngular(() => this.runSync(false, 'online'));
    });
    // Pause/resume the whole prefetch queue on the latency-aware slow-connection signal. A run
    // already in progress on a connection that turns out to be too slow stops enqueuing new
    // work immediately rather than grinding the main thread for minutes; it picks back up if
    // the connection recovers.
    effect(() => {
      if (this.networkStatusService.slowConnection()) this.prefetchGate.pause();
      else this.prefetchGate.resume();
    });
    // Phase 2 (2026-09-29): the phone app can sit in the background for hours while online — no
    // reconnect ever happens, so nothing refreshed the offline copy before the user walked out of
    // signal. Coming back to the app now refreshes it (and sends anything waiting), at most once
    // per RESUME_MIN_GAP_MS.
    try {
      App.addListener('resume', () => {
        if (!this.networkStatusService.isOnline) return;
        this.syncEngine.trySync();
        if (this.liveSyncWanted || Date.now() - this.lastSyncStartedAt > OfflinePrefetchService.RESUME_MIN_GAP_MS) {
          this.zone.runOutsideAngular(() => this.runSync(false, 'resume'));
        }
      }).catch(() => undefined);
    } catch { /* @capacitor/app missing — reconnect-triggered refresh still works */ }
  }

  private static readonly RESUME_MIN_GAP_MS = 10 * 60 * 1000;
  private static readonly TIDY_MIN_GAP_MS = 6 * 60 * 60 * 1000;
  private static readonly LAST_TIDY_KEY = 'll_offline_last_tidy';

  /** "Sync now" (header) — send what is waiting, then refresh the offline copy, skipping the short
   *  duplicate-run guard. Resolves when both are done. */
  async refreshNow(): Promise<void> {
    await this.syncEngine.syncNow(true).catch(() => undefined);
    await this.runSync(true, 'manual');
  }

  /** Offline file clean-up (see tidyOfflineFiles), at most every TIDY_MIN_GAP_MS. */
  private async maybeTidyFiles(): Promise<void> {
    try {
      const last = Number(localStorage.getItem(OfflinePrefetchService.LAST_TIDY_KEY)) || 0;
      if (Date.now() - last < OfflinePrefetchService.TIDY_MIN_GAP_MS) return;
      localStorage.setItem(OfflinePrefetchService.LAST_TIDY_KEY, String(Date.now()));
      const { removed, keptBytes } = await tidyOfflineFiles(await this.pinnedFileKeys());
      if (removed) this.logger.info(`[OfflinePrefetchService] offline file clean-up removed ${removed} file(s); ${Math.round(keptBytes / 1048576)} MB kept`);
    } catch (err) {
      this.logger.warn('[OfflinePrefetchService] offline file clean-up failed', err);
    }
  }

  /** Keys of the saved document files of every unit pinned with "Keep offline": the clean-up must never remove these.
   *  Read from what is already saved on the phone (the unit detail gives its tally lines, each line gives its document list). */
  private async pinnedFileKeys(): Promise<Set<string>> {
    const keys = new Set<string>();
    const pins = await getTuPins().catch(() => []);
    for (const pin of pins) {
      const entry = await offlineDb.detailCache.get(`tu:encoded:${pin.encodedId}`).catch(() => undefined);
      const detail: any = entry?.data;
      if (!detail) continue;
      const masterIds: number[] = [detail.tallyMaster?.id, ...(detail.additionalLines || []).map((l: any) => l.tallyMasterId)].filter((id: any) => !!id);
      for (const masterId of masterIds) {
        const docsEntry = await offlineDb.detailCache.get(scopedKey(`tallyDocuments:${masterId}`)).catch(() => undefined);
        const docs: any[] = Array.isArray(docsEntry?.data) ? (docsEntry!.data as any[]) : [];
        docs.forEach((d: any) => { if (d?.id) keys.add(`tallyDocImage:${d.id}`); });
      }
    }
    return keys;
  }

  // Confirmed live 2026-08-05: a real reconnect can flap through offline->online->offline->
  // online within the same handful of seconds (radio re-associating, DHCP, etc.) before
  // settling — each online edge independently starts its own fresh 8s debounce in
  // NetworkStatusService (see its own comment; that debounce only collapses REPEATED online
  // events within one already-pending timer, not two separate edges with an offline blip
  // between them). Observed as two full sync batches (every list + every detail/photo
  // prefetch, doubled) firing ~7.7s apart. This is a narrow duplicate-run guard, NOT a
  // reintroduction of the old broad cooldown above — it only blocks a second run from
  // starting this soon after the last one *started*, it does not throttle genuinely
  // separate reconnects (a real subsequent disconnect/reconnect in the field is essentially
  // always further apart than this).
  private static readonly DUPLICATE_RUN_COOLDOWN_MS = 20_000;
  private lastSyncStartedAt = 0;

  async runSync(force = false, reason = 'online'): Promise<void> {
    if (!isOfflineCapableNative()) return;
    if (!this.networkStatusService.isOnline) return; // defensive: a flap may have resolved false again during the debounce window
    // isSlowConnection is effectiveType-only (2g/slow-2g). Also honour the latency-aware
    // slowConnection() signal (rolling average of real request latency): a normal wifi that is
    // just congested/flaky reports "4g" but still can't handle a prefetch storm — that is
    // exactly the condition that froze the app on-device. When it clears, the next
    // online-transition tick re-runs this.
    if (this.networkStatusService.isSlowConnection || this.networkStatusService.slowConnection()) return;
    if (this.inFlight) return;
    if (!force && Date.now() - this.lastSyncStartedAt < OfflinePrefetchService.DUPLICATE_RUN_COOLDOWN_MS) return;

    // Resolve the session FIRST and run the SAME different-user wipe check AuthSessionGuard runs,
    // awaited, before touching any cached list/detail data below — and BEFORE the onboarding-route
    // skip further down. This must run unconditionally, regardless of route: account-setup/*
    // and email-verification* have NO canActivate guard at all (AuthSessionGuard never runs there),
    // so this effect()-driven call is the ONLY thing that can wipe a previous tenant's stale cache
    // during the entire signup -> verify-email -> account-details -> company-details window.
    // Found 2026-08-04 via a real repeating "Not found" error burst for a fresh login — see
    // wipeOfflineDataIfDifferentUser's own comment in offline-db.ts for that incident.
    // CONFIRMED LIVE 2026-08-27 (do not move this back below the onboarding-route check): an
    // earlier same-day fix added that onboarding-route skip ABOVE this wipe call to stop prefetch
    // from hammering endpoints with no company yet (TEST_CASES\27-08-2026\prod-exception-log-fixes.md
    // item 3) — but that also silently skipped THIS wipe for a fresh signup on a device previously
    // used by a different real tenant, since AuthSessionGuard never runs on these routes either.
    // Net effect: tenant-3's stale cached business-partner data survived, untouched, through an
    // entire onboarding session for a brand-new test2 account, and surfaced once onboarding
    // finished. The wipe itself only touches localStorage + Dexie .clear() (no extra network calls
    // beyond the one syncSessionForGuard() already makes), so it is cheap and safe to run even
    // mid-onboarding — only the expensive prefetch batch below needs to stay behind the
    // onboarding-route check.
    const sessionCallStart = Date.now();
    const sessionResult = await firstValueFrom(this.permissionService.syncSessionForGuard()).catch(() => null);
    const sessionCallMs = Date.now() - sessionCallStart;
    this.logger.info('[OfflinePrefetchService] runSync session check', JSON.stringify({
      outcome: sessionResult?.outcome ?? null,
      hasEmail: !!sessionResult?.email,
      route: this.router.url,
      isOnboardingRoute: this.isOnboardingInProgressRoute(),
      sessionCallMs,
    }));
    if (sessionResult?.outcome === 'live' && sessionResult.email) {
      await wipeOfflineDataIfDifferentUser(sessionResult.email, sessionResult.tenantId);
    }
    // From here on a logout / session end must stop this run before it even starts (the identity wipe above bumps the
    // run id itself, so the id is read after it).
    const preEpoch = this.runEpoch;

    // A user who verified email but hasn't finished Company Details yet has no company/tenant at
    // all, so every one of these prefetch calls (business partners, loading sites, etc.) 404s or
    // 500s. Skip only the prefetch batch while still on the onboarding wizard — a real sync will
    // run once it's online again after onboarding finishes and lands somewhere real. The wipe
    // above already ran regardless of this check — see its own comment.
    if (this.isOnboardingInProgressRoute()) return;

    // The one authenticated call every run already makes is also a live latency probe. If it
    // came back slow, the connection cannot handle the full prefetch fan-out right now without
    // starving the UI — skip this run entirely (the wipe above already ran). The next
    // online-transition tick, or a genuinely faster connection, retries. This is the fast,
    // first-request signal that the rolling-average slowConnection() check can't give on a cold
    // start (it needs a few samples first).
    if (sessionCallMs > 2500) {
      this.logger.error('[OfflinePrefetchService] session probe slow (' + sessionCallMs + 'ms) — skipping prefetch this run');
      return;
    }

    // Let the current screen finish its first render before starting a background storm of
    // requests + IndexedDB writes. On a cold start the effect() in the constructor fires this
    // almost immediately, landing the prefetch right on top of the landing page's heavy first
    // paint. requestIdleCallback resolves once the main thread has been idle; the timeout caps
    // the wait so a permanently-busy thread can't defer the sync forever.
    await this.waitForIdle();
    if (preEpoch !== this.runEpoch) return; // the session ended while waiting

    this.lastSyncStartedAt = Date.now();
    this.inFlight = true;
    // This run's id: a logout / session end / identity wipe during the run bumps runEpoch and the run stops.
    const epoch = this.runEpoch;
    this.tuDone = 0;
    this.tuTotal = 0;
    this.bundleUnavailable = false;
    this.bundleFailures = 0;
    this.bundleRequests = 0;
    this.stats = { trigger: reason, units: 0, complete: 0, incomplete: 0, gaveUp: 0, reasons: {} };
    const runStartedAt = Date.now();
    setAnswerTracking(true); // from now the HTTP layer notes which calls the server really answered
    this.liveSyncWanted = false; // this run brings the offline copy up to date as of now; a change that arrives during it asks again
    // Reset every row to pending at the start of each run — a prior run's leftover done/error
    // states must not bleed into the next one. zone.run so the strip repaints (runSync is
    // otherwise entirely outside the zone — see setAreaStatus()).
    this.zone.run(() => this.syncAreas.set(INITIAL_SYNC_AREAS.map(a => ({ ...a }))));

    try {
      await Promise.allSettled([
        // Master dropdowns + dashboard stats complete silently — no checklist row of their own.
        this.swallow(() => this.once(this.masterCacheService.getBuyers())),
        this.swallow(() => this.once(this.masterCacheService.getSuppliers())),
        this.swallow(() => this.once(this.masterCacheService.getProducts())),
        this.swallow(() => this.once(this.masterCacheService.getLoadingSites())),
        this.swallow(() => this.once(this.masterCacheService.getTransportModes())),
        this.swallow(() => this.once(this.masterCacheService.getFumigationTypes())),
        this.swallow(() => this.once(this.masterCacheService.getCountries())),
        // Custom-Made request dialog's open-lots list — tenant-wide, no params, same gate as the
        // other inventory-only prefetch calls below. Silent (no checklist row) like the master
        // dropdowns above; gated on inventoryModuleService.visible since Custom-Made only exists
        // for Inventory-enabled tenants.
        // 2026-09-17: also needs a right the server accepts for open batches (Overview, Processing or
        // Process Inventory) — a refused prefetch still pops the global "Access Denied" dialog.
        this.inventoryModuleService.visible
          && this.permissionService.hasAny(['INVENTORY_OVERVIEW_VIEW', 'INVENTORY_PROCESSING_VIEW', 'INVENTORY_PROCESS'])
          ? this.swallow(() => this.once(this.inventoryLotService.listOpenLotsCached()))
          : Promise.resolve(),
        this.runArea('common.syncAreaBusinessPartners', () => this.businessPartnerService.prefetchDefaultList()),
        this.runArea('common.syncAreaProducts', () => this.productService.prefetchDefaultList()),
        this.runArea('common.syncAreaLoadingSites', () => this.loadingSiteService.prefetchDefaultList()),
        this.runArea(OfflinePrefetchService.TU_AREA, async () => {
          const newestTen = await this.prefetchTuList(epoch);
          if (epoch !== this.runEpoch) return;
          await this.prefetchTuWindow(newestTen, epoch);
          this.flushTuCount(epoch);
        }),
        this.runArea('common.syncAreaDashboard', () => this.prefetchDashboardStats()),
      ]);
      // The session ended (or the identity changed) during the run: nothing below belongs to the new session.
      if (epoch !== this.runEpoch) return;
      this.logRunSummary(runStartedAt);
      // "Last synced" — this refresh finished while still online (Phase 2).
      if (this.networkStatusService.isOnline) {
        this.zone.run(() => this.syncEngine.markSynced());
      }
      await this.maybeTidyFiles();
    } finally {
      // A run that was cancelled must not clear the flag of the run that replaced it.
      if (epoch === this.runEpoch) {
        this.inFlight = false;
        setAnswerTracking(false);
        // Live changes that arrived while this run was going: one follow-up run (after the settle time / minimum gap).
        if (this.liveSyncWanted) this.scheduleLiveSync();
      }
    }
  }

  // On first-ever sync (modifiedSince=null) requests exactly the same size=10&page=0 as before,
  // matching ViewTransportUnitComponent.loadData()'s default query, so the merged cache stays a
  // drop-in replacement for what that component reads offline. On a delta sync, size is widened
  // to comfortably capture every row touched since the last sync in one page.
  private fetchTuPage(modifiedSince: string | null): Observable<HttpResponse<Page<any>>> {
    let url = `${AppConstants.API_URL}api/v1/transportunits/paginated?page=0&size=${modifiedSince ? 200 : 10}&sortBy=createdDate&sortDir=desc&globalFilter=`;
    if (modifiedSince) url += `&modifiedSince=${encodeURIComponent(modifiedSince)}`;
    return this.http.get<Page<any>>(url, { observe: 'response' });
  }

  // No dedicated TU-list service exists today — ViewTransportUnitComponent.loadData() calls
  // this.http.get directly — so fetchTuPage() replicates that exact default URL/cache-key.
  // Delegates to runDeltaListSync (Increment 6) for the list itself, then warms each row's own
  // detail cache (tu:encoded:${encodedId}, via TransportUnitService.getByEncodedId) the same way
  // businessPartnerService/productService/loadingSiteService's prefetchDefaultList already do for
  // their own lists — without this, a TU never individually opened still renders blank offline
  // even though its row was in the prefetched list. Beyond that, also warms every tally line's
  // own tabs (details/settings/documents/chart/summary) and inventory movements — see
  // prefetchTallyLinesFor() — so the whole tallysheet view works offline, not just the header.
  //
  // TU-specific efficiency requirement (Increment 6): a full tallysheet can be thousands of rows,
  // so re-walking every TU's tabs on every reconnect (as the pre-delta-sync version did) is real
  // wasted time/bandwidth. On a delta sync, a row absent from touchedIds is untouched since the
  // last sync — its tallysheet was already fully prefetched and hasn't changed — so only rows
  // that are genuinely new or modified get their tabs re-walked.
  private async prefetchTuList(epoch: number): Promise<Set<number>> {
    const canonicalUrl = `${AppConstants.API_URL}api/v1/transportunits/paginated?page=0&size=10&sortBy=createdDate&sortDir=desc&globalFilter=`;
    const result = await this.swallowResult(() => runDeltaListSync<any>({
      entity: 'transportUnits',
      cacheKey: `tuList:${canonicalUrl}`,
      fetchPage: (modifiedSince) => this.fetchTuPage(modifiedSince),
      deletionsEntityType: 'TRANSPORT_UNIT',
      mergeConfig: {
        getId: (tu) => tu.id,
        getCreatedDate: (tu) => tu.createdDate ?? '',
        windowSize: 10,
      },
      http: this.http,
    }));
    if (!result) return new Set<number>();
    // The session ended while the list was loading: do nothing more for it.
    if (epoch !== this.runEpoch) return new Set<number>();
    if (result.evictedRows.length) {
      await Promise.allSettled(result.evictedRows.map((row: any) => this.purgeDeletedTu(row)));
    }
    const rows = result.page.content || [];
    // Units to walk now (on a delta sync only the new / changed ones). Counted at once so the Stock Units row shows
    // "0 / n" as soon as the list is back instead of after the whole first batch.
    const toWalk = rows.filter((row: any) => !(result.isDelta && !result.touchedIds.has(row.id)));
    this.addTuTotal(toWalk.length, epoch);
    const savedIds = new Set<number>();
    await this.warmInChunks(toWalk, epoch, async (row: any) => {
      try {
        const saved = await this.prefetchTuDetailAndTabs(row);
        if (epoch === this.runEpoch) this.noteUnit(saved);
        if (saved) savedIds.add(row.id);
      } finally {
        this.bumpTuDone(epoch);
      }
    });
    // A unit that was walked but is NOT completely saved stays out of this set, so the window pass below tries it
    // again and no mark claims it is saved. Units not walked (unchanged on a delta sync) were saved earlier.
    const walkedIds = new Set<number>(toWalk.map((r: any) => r.id));
    return new Set<number>(rows.map((r: any) => r.id).filter((id: any) => id != null && (!walkedIds.has(id) || savedIds.has(id))));
  }

  // OFFLINE_PLAN.md Phase 2 / D7 (2026-09-29) — the offline window beyond the newest 10: every transport
  // unit created in the last 90 days, still open, or pinned ("Keep offline"). Saved as list rows (the
  // Transport Units screen searches/sorts/pages them offline — see offline-window.ts), and each unit's
  // detail + tally rows are warmed only when new to the window or changed since (fingerprint), so a
  // normal refresh costs a handful of requests, not hundreds. No photo/document files here — those are
  // fetched when opened (D7). A unit that left the window has its offline copy freed, except the
  // newest 10, which prefetchTuList() keeps.
  private async prefetchTuWindow(newestTen: Set<number>, epoch: number): Promise<void> {
    const pins = await getTuPins().catch(() => []);
    const pinnedIds = pins.map((p) => p.id).filter((id) => id > 0);
    const pinnedSet = new Set<number>(pinnedIds); // pinned units also keep their document files up to date (not only at pin time)
    let url = `${AppConstants.API_URL}api/v1/transportunits/offline-window?days=${TU_WINDOW_DAYS}&max=${TU_WINDOW_MAX}`;
    if (pinnedIds.length) url += `&pinnedIds=${pinnedIds.join(',')}`;
    const rows = await this.swallowResult(() => this.once(this.http.get<any[]>(url)));
    if (!Array.isArray(rows)) return; // older server, or offline mid-run — keep the last window as is
    // The session ended while the list was loading: write nothing for it (it could be another user's cache by now).
    if (epoch !== this.runEpoch) return;

    await saveTuWindow(rows);
    const previous = await getTuWindowMarks().catch(() => ({} as Record<string, string>));
    if (epoch !== this.runEpoch) return;
    const next: Record<string, string> = {};
    // Units already warm and unchanged are skipped; the rest are counted live in the strip ("45 / 180").
    const toWarm: any[] = [];
    for (const row of rows) {
      if (row?.id == null) continue;
      const fp = tuFingerprint(row);
      const prev = previous[String(row.id)];
      // Saved: the unit is unchanged since it was saved completely (mark = fingerprint), or it was accepted as it is after two
      // incomplete runs (mark = fingerprint + "|t2", kept until the unit changes).
      if (newestTen.has(row.id) || prev === fp || prev === `${fp}|t2`) next[String(row.id)] = (prev === `${fp}|t2` && !newestTen.has(row.id)) ? prev : fp;
      else toWarm.push(row);
    }
    this.addTuTotal(toWarm.length, epoch);

    // Progress is saved while the run goes (every 5 finished units), so a run that stops half way — logout, app closed,
    // lost network — continues from there next time instead of warming every unit again. The saved copy is the old
    // marks plus the new ones: a unit that changed keeps its OLD mark until it is warmed again (so it is retried), and
    // a unit that left the window keeps its mark so the clean-up below still finds it next time. Saves are chained so an
    // older one can never finish after (and overwrite) a newer one, and none is written once the session has ended.
    let saveChain: Promise<unknown> = Promise.resolve();
    let sinceSave = 0;
    const saveProgress = (): void => {
      if (epoch !== this.runEpoch) return;
      const snapshot: Record<string, string> = { ...previous, ...next };
      saveChain = saveChain
        .then(() => (epoch === this.runEpoch ? saveTuWindowMarks(snapshot) : undefined))
        .catch(() => undefined);
    };

    await this.warmInChunks(toWarm, epoch, async (row: any) => {
      if (epoch !== this.runEpoch) return;
      try {
        const saved = await this.prefetchTuDetailAndTabs(row, pinnedSet.has(row.id));
        if (epoch !== this.runEpoch) return;
        this.noteUnit(saved);
        const fp = tuFingerprint(row);
        if (saved) {
          next[String(row.id)] = fp;
        } else {
          // Not completely saved: tried again on the next run, but only once more. After two incomplete runs the unit is accepted as it is
          // (and counted in the run summary), so a unit that can never be saved completely does not make every run fetch it again.
          const tries = previous[String(row.id)] === `${fp}|t1` ? 2 : 1;
          next[String(row.id)] = `${fp}|t${tries}`;
          if (tries >= 2) this.stats.gaveUp++;
        }
        if (++sinceSave >= 5) {
          sinceSave = 0;
          saveProgress();
        }
      } finally {
        this.bumpTuDone(epoch);
      }
    });
    if (epoch !== this.runEpoch) return;
    const inWindow = new Set(rows.map((r: any) => String(r?.id)));
    const left = Object.keys(previous).filter((id) => !inWindow.has(id) && !newestTen.has(Number(id)));
    await Promise.allSettled(left.map((id) => this.purgeDeletedTu({ id: Number(id) })));
    await saveChain;
    if (epoch !== this.runEpoch) return;
    await saveTuWindowMarks(next);
  }

  /** Warms one transport unit for offline use (detail + every tally line's tabs). withFiles = also
   *  download its tally documents/photos (only the newest 10 do, see prefetchTuWindow). */
  private async prefetchTuDetailAndTabs(row: any, withFiles = true): Promise<boolean> {
    if (!row.encodedId) return false;
    const since = Date.now();
    // Same key TransportUnitService.getByEncodedId() saves under (it does not add the tenant prefix).
    const detail = await this.warm(() => this.once(this.transportUnitService.getByEncodedId(row.encodedId)), `tu:encoded:${row.encodedId}`, since, OfflinePrefetchService.unitPath(row.encodedId));
    if (!detail.value) return false;
    const linesComplete = await this.prefetchTallyLinesFor(detail.value, withFiles, since);
    return detail.complete && linesComplete;
  }

  // "Is this saved on the phone, and from THIS run?" (offline availability check, 2026-10-03)
  //
  // The cache helpers (DetailCacheService / TransportUnitService) answer a failed request (no network, 5xx) with the OLD saved
  // copy, and errors here were swallowed. Both looked like success, so a unit was marked "saved" while its tally rows, settings or
  // documents were missing or stale, and nothing retried it until the unit changed. A request now counts as complete only when:
  //  - it gave an answer (not skipped by the paused / cancelled queue), AND
  //  - the saved copy was written during this unit's run (fetchedAt >= since), so an old copy returned by a fallback does not count.
  // An answer of "no" from the server (403 no right, 404 gone ...) is final: nothing to save and nothing to retry.
  // No answer, 5xx, 429 or a skipped request: not complete, the unit is retried on the next sync.
  private async warm<T>(factory: () => Promise<T>, freshKey: string, since: number, url: string): Promise<{ complete: boolean; value?: T }> {
    let value: T;
    try {
      value = await this.prefetchGate.run(factory);
    } catch (err) {
      if (this.isDefiniteAnswer(err)) return { complete: true };
      this.noteIncomplete('no-answer');
      return { complete: false };
    }
    if (value === undefined) { this.noteIncomplete('skipped'); return { complete: false }; }
    // Did the server (or the bundle) really answer THIS call during this unit's run? A failed call is answered with the OLD saved copy by the
    // cache helpers, which is not an answer. With proof, wait patiently for the saved copy to show up (slow database); without proof, only a
    // short look (an old copy returned by a fallback never turns fresh).
    const answered = wasAnsweredSince(url, since);
    const fresh = await this.isFresh(freshKey, since, answered ? OfflinePrefetchService.WRITE_WAIT_MS : OfflinePrefetchService.NO_ANSWER_WAIT_MS);
    if (!fresh) this.noteIncomplete(answered ? 'write-not-visible' : 'no-answer');
    return { complete: fresh, value };
  }

  private isDefiniteAnswer(err: unknown): boolean {
    return err instanceof HttpErrorResponse && err.status >= 400 && err.status < 500 && err.status !== 408 && err.status !== 429;
  }

  /** The cache write is not awaited by the callers, so look twice (a short pause in between) before calling it stale. */
  private async isFresh(key: string, since: number, waitMs: number): Promise<boolean> {
    const deadline = Date.now() + waitMs;
    for (;;) {
      const entry = await offlineDb.detailCache.get(key).catch(() => undefined);
      if (entry && (entry.fetchedAt ?? 0) >= since) return true;
      if (Date.now() >= deadline) return false;
      await new Promise<void>((resolve) => setTimeout(resolve, 120));
    }
  }

  // The paths the services call (the bundle echoes the same ones): used to look up "did the server answer this path".
  private static unitPath(encodedId: string): string { return `/api/v1/transportunits/by-encoded/${encodeURIComponent(encodedId)}`; }
  private static tallyPath(kind: string, masterId: number): string { return `/api/v1/tally/${kind}/${masterId}`; }

  /** "Keep offline" on a unit while online — save it for offline use right away (don't wait for the next
   *  refresh). Files included: the user asked for this one specifically. */
  async keepTuOfflineNow(row: any): Promise<void> {
    if (!isOfflineCapableNative() || !this.networkStatusService.isOnline) return;
    await this.prefetchTuDetailAndTabs(row, true);
  }

  // A deleted TU's tallysheet cascade (paginated rows, doc blobs, summaries) is by far the
  // largest offline storage footprint in the app — evicting the TU from the cached LIST alone
  // (already handled by mergeDeltaIntoWindow) would leave all of that stranded in IndexedDB
  // forever. The evicted list row itself doesn't carry the tallyMasterId chain (only the detail
  // fetch does) — read this TU's own (now-stale) cached detail record first to discover it,
  // same tallyMasterId key shapes prefetchOneTallyLine() writes, deleting instead of writing.
  // Document image blobs (tallyDocImage:${docId}) are keyed by document id, not tallyMasterId —
  // can't be derived without an extra fetch the deleted TU can no longer serve, so those are
  // left as a small residual orphan (bounded by the existing 10MB per-file cap), same known
  // limitation as BP/Product/Loading Site attachment blobs (see purgeDeletedDetail()).
  private async purgeDeletedTu(row: any): Promise<void> {
    if (!row?.id) return;
    const detailKey = `tu:id:${row.id}`;
    const entry = await offlineDb.detailCache.get(detailKey);
    const detail = entry?.data as any;

    const keysToDelete: string[] = [detailKey, `tuInventoryMovements:${row.id}`];
    const encodedId = detail?.encodedId ?? row.encodedId;
    if (encodedId) keysToDelete.push(`tu:encoded:${encodedId}`);

    const tallyMasterIds: number[] = [];
    if (detail?.tallyMaster?.id) tallyMasterIds.push(detail.tallyMaster.id);
    (detail?.additionalLines || []).forEach((l: any) => { if (l.tallyMasterId) tallyMasterIds.push(l.tallyMasterId); });

    await Promise.all(tallyMasterIds.map(async (tmId) => {
      keysToDelete.push(`tallySettings:${tmId}`, `tallySummary:${tmId}`, `tallyChart:${tmId}`, `tallySawnSquare:${tmId}`);
      const detailPages = await offlineDb.detailCache.where('key').startsWith(`tallyDetails:${tmId}:`).primaryKeys();
      keysToDelete.push(...(detailPages as string[]));
    }));

    await Promise.all(keysToDelete.map((k) => offlineDb.detailCache.delete(k).catch(() => {})));
  }

  // Mirrors DashboardTallysheetNewComponent's own tallysheetLines getter exactly (main
  // tallyMaster.id/.types plus each additionalLines[].tallyMasterId/.types) — reading straight
  // off the detail response already in hand, no extra fetch needed to know the line ids.
  private async prefetchTallyLinesFor(detail: any, withFiles: boolean, since: number): Promise<boolean> {
    const lines: { tallyMasterId: number; types: string }[] = [];
    if (detail?.tallyMaster) {
      lines.push({ tallyMasterId: detail.tallyMaster.id, types: detail.tallyMaster.types });
    }
    (detail.additionalLines || []).forEach((line: any) => {
      lines.push({ tallyMasterId: line.tallyMasterId, types: line.types });
    });

    const linePromises = lines.map((line) => this.prefetchOneTallyLine(line, withFiles, since).catch(() => false));

    // 2026-09-16 -- also requires INVENTORY_ACCESS now, not just the tenant module switch. This
    // runs silently in the background with no user action behind it; before this check, a user
    // without the right would hit a now-enforced 403 here and the global HTTP interceptor's
    // generic "Access Denied" dialog would pop up completely out of context, unrelated to
    // whatever they were actually doing at the time.
    const inventoryPromise = (this.inventoryModuleService.visible && this.permissionService.hasAny(['INVENTORY_ACCESS']) && detail.id)
      ? this.swallow(() => this.once(this.inventoryService.getMovementsForTu(detail.id)))
      : Promise.resolve();

    // The inventory movements are optional (rights / module dependent): they never decide whether the unit counts as saved.
    const [lineResults] = await Promise.all([Promise.all(linePromises), inventoryPromise.catch(() => undefined)]);
    return lineResults.every(Boolean);
  }

  // Details/settings/documents are shared across round and square; chart+summary are round-only,
  // sawn-square-summary is square-only — matches exactly which endpoints each tab type calls
  // (see round-tally-summary.component.ts vs square-summary-tallysheet-new.component.ts).
  // Returns true only when every part of this tally line is saved (see warm()). The keys below are the ones the services save
  // under (tallysheet-list.service.ts: tenant-prefixed through DetailCacheService; transport-unit.service.ts: summaries, no prefix).
  private async prefetchOneTallyLine(line: { tallyMasterId: number; types: string }, withFiles: boolean, since: number): Promise<boolean> {
    if (!line.tallyMasterId) return true;
    const id = line.tallyMasterId;
    const calls: Promise<boolean>[] = [
      this.prefetchAllTallyDetails(id, since),
      this.warm(() => this.once(this.tallysheetListService.fetchSettings(id)), scopedKey(`tallySettings:${id}`), since, OfflinePrefetchService.tallyPath('settings', id)).then((r) => r.complete),
      // Without files: still the document LIST (small), so the Documents tab shows what exists offline.
      withFiles
        ? this.prefetchDocsWithImages(id, since)
        : this.warm(() => this.once(this.tallysheetListService.fetchDocuments(id)), scopedKey(`tallyDocuments:${id}`), since, OfflinePrefetchService.tallyPath('documents', id)).then((r) => r.complete),
    ];
    if (line.types === 'ROUND') {
      calls.push(this.warm(() => this.once(this.transportUnitService.getChartData(id)), `tallyChart:${id}`, since, OfflinePrefetchService.tallyPath('chart-data', id)).then((r) => r.complete));
      calls.push(this.warm(() => this.once(this.transportUnitService.getSummary(id)), `tallySummary:${id}`, since, OfflinePrefetchService.tallyPath('summary', id)).then((r) => r.complete));
    } else if (line.types === 'SQUARE') {
      calls.push(this.warm(() => this.once(this.transportUnitService.getSawnSquareSummary(id)), `tallySawnSquare:${id}`, since, OfflinePrefetchService.tallyPath('sawn-square-summary', id)).then((r) => r.complete));
    }
    const results = await Promise.all(calls.map((c) => c.catch(() => false)));
    return results.every(Boolean);
  }

  // Was previously a single fetchDetails(masterId, 0, 1000) call — silently truncating any
  // tally sheet with more than 1000 rows, since that's genuinely just the first page, not
  // "everything." Paginates through every page instead. PAGE_SIZE=1000 is not an arbitrary
  // choice — it's the exact same `pageSize` TallysheetListRoundComponent/
  // TallysheetSquareListComponent's own loadMore() already uses (confirmed in both files), so
  // each page lands under fetchDetails()'s existing cache key
  // (tallyDetails:${masterId}:${page}:1000, via DetailCacheService.cacheThrough) — exactly
  // what the live grid's own loadMore() requests page-by-page. No new cache key and no
  // component read-path change needed: offline pagination just gets real cache hits, all the
  // way through however many pages actually exist.
  private async prefetchAllTallyDetails(tallyMasterId: number, since: number): Promise<boolean> {
    const PAGE_SIZE = 1000;
    const MAX_PAGES = 50; // safety valve: 50,000 rows — if a real tally ever needs more than
    // this, something is wrong upstream; stop and let it log rather than loop forever.
    for (let page = 0; page < MAX_PAGES; page++) {
      const result = await this.warm(
        () => this.once(this.tallysheetListService.fetchDetails(tallyMasterId, page, PAGE_SIZE)),
        scopedKey(`tallyDetails:${tallyMasterId}:${page}:${PAGE_SIZE}`), since,
        `${OfflinePrefetchService.tallyPath('details', tallyMasterId)}?page=${page}&size=${PAGE_SIZE}`);
      // A page that failed means the rows after it are missing offline: the unit is NOT complete.
      if (!result.complete) return false;
      const rows = result.value;
      if (!rows?.length) break;
      if (rows.length < PAGE_SIZE) break; // partial page — exhausted
    }
    return true;
  }

  // fetchDocuments() already caches the metadata list (write-through, same as everywhere
  // else); this additionally pulls each document's actual bytes (any file type, not just
  // images — matches BusinessPartnerService.cacheAttachmentBlobs/LoadingSiteService.
  // cacheDocumentBlobs, which already do this) so the Documents tab can render/download the
  // real file offline instead of just showing a dead link. Same 10MB size cap as those two;
  // bounded to whatever this pass already prefetches (top TUs' tally lines), not a full
  // history walk.
  private async prefetchDocsWithImages(tallyMasterId: number, since: number): Promise<boolean> {
    const listed = await this.warm(() => this.once(this.tallysheetListService.fetchDocuments(tallyMasterId)), scopedKey(`tallyDocuments:${tallyMasterId}`), since, OfflinePrefetchService.tallyPath('documents', tallyMasterId));
    const docs = listed.value;
    if (!docs?.length) return listed.complete;
    const cacheableDocs = docs.filter((doc: any) => doc?.id && !(doc.size && doc.size > MAX_CACHE_FILE_SIZE));
    await Promise.allSettled(cacheableDocs.map((doc: any) => this.swallow(
      () => this.once(this.http.get(`${this.tallysheetListService.apiUrl}/documents/${doc.id}/content`, { responseType: 'blob' }))
        .then((blob) => offlineDb.detailCache.put({ key: `tallyDocImage:${doc.id}`, data: blob, fetchedAt: Date.now() })),
    )));
    // The document LIST decides "complete"; a single file that did not download is best effort (it is fetched when opened).
    return listed.complete;
  }

  // FIXED 2026-08-27: was calling DashboardV5Service.getStats(), which does its own client-side
  // forkJoin including an UNGATED GET /api/v1/inventory/summary — a 403 from a tenant without
  // Inventory access reaches this app-wide global HTTP interceptor before this method's own
  // swallow() ever gets a chance to run, popping the blocking "Plan Limit Reached" upgrade
  // dialog on literally every app open (this effect() fires on every online-transition — see
  // the constructor). V5's own route has been fully retired since 2026-08-01 (dashboard-v5
  // redirects to dashboard-v7 — see app-routing.module.ts) — the LIVE dashboard is V7, whose own
  // DashboardV7Service just forwards to this same /api/v1/dashboard-v6/stats endpoint, which
  // embeds inventory data SERVER-SIDE behind a try/catch (see DashboardV6ServiceImpl.getStats())
  // instead of a separate client-side call — it can never 403 the frontend, tenant plan or not.
  // Cache key changed from 'dashV5:' to 'dashV7:' (matching toLocalDateString, not
  // toISOString().split('T')[0] — the old UTC-based formatting could disagree with the live V7
  // screen's own local-date-based key for any timezone ahead of UTC, e.g. IST) so this
  // background prefetch actually warms the SAME cache DashboardV7Component reads from, instead
  // of silently caching under a key nothing ever reads.
  private prefetchDashboardStats(): Promise<void> {
    const to = new Date();
    const from = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    const fromStr = toLocalDateString(from)!;
    const toStr = toLocalDateString(to)!;
    return this.swallow(() => this.once(this.listCacheService.cacheThrough(
      `dashV7:${fromStr}_${toStr}`,
      () => this.dashboardV6Service.getStats(fromStr, toStr),
    )));
  }

  // Now takes a FACTORY, not a started promise, and runs it through PrefetchGateService —
  // bounded concurrency, outside Angular's zone, yielding between units. Every leaf HTTP call in
  // this service's prefetch tree goes through swallow()/swallowResult(), so this one change caps
  // the whole transport-unit / tallysheet fan-out (which can be thousands of requests deep) the
  // same way the three list services' own prefetchDefaultList() do. See PrefetchGateService for
  // the freeze that made this necessary. The orchestration around these calls (Promise.allSettled,
  // .map, for-loops) stays un-gated on purpose — only the leaves are gated, so nothing ever
  // awaits a nested gate slot and there is no deadlock.
  private async swallow(factory: () => Promise<unknown>): Promise<void> {
    try {
      await this.prefetchGate.run(factory);
    } catch {
      // Best-effort — same fire-and-forget convention as prefetchDetailCache elsewhere.
    }
  }

  // Same as swallow(), but keeps the resolved value (null on failure) instead of discarding it —
  // needed by prefetchTuList() to read the fetched rows back out.
  private async swallowResult<T>(factory: () => Promise<T>): Promise<T | null> {
    try {
      return await this.prefetchGate.run(factory);
    } catch {
      return null;
    }
  }
}
